// The login screen: an address, then a code, then an authenticator if the account
// has one.
//
// THE SERVER MARK IS ON THIS SCREEN TOO, and it is the one screen where it would
// be easy to leave it off, because Chrome only wraps signed-in surfaces. A login
// form is where somebody decides whether they are in the right place, so a
// console that only admits to being the rehearsal one after you have signed in
// has told you too late.
//
// THREE STEPS AND NO WAY TO SKIP ONE. The step is a value from api/signIn.ts
// rather than three booleans, so "showing the code field and the authenticator
// field at once" is not a state this component can be in — which matters because
// a form showing an authenticator box before the code has been checked would be
// announcing whether the account has a factor, and that is the enumeration
// channel the whole login path is built around.
//
// FOUR STAGES ARE DRAWN AND THERE ARE THREE FORMS, WHICH IS DELIBERATE AND IS A
// SECURITY PROPERTY. SignInSteps.tsx draws the same four stages to every address
// alive, including the authenticator stage for accounts that have none, because a
// strip whose length depended on the account would answer "which of these
// addresses has two-factor turned on" to anybody who typed one in — before a code
// had been checked, which is the one thing this whole screen is built to prevent.
// api/signIn.ts computes the states from the step and from nothing else, so there
// is no argument in that function that the account could reach. The fourth stage
// is the console opening, which is App.tsx replacing this surface rather than
// anything drawn here.
//
// A REFUSAL AND A PROGRESSION ARE TOLD APART BY narrateRefusal, AND GETTING THAT
// WRONG IS WHAT THIS SCREEN SHIPPED WITH. The service answers the right emailed
// code on an account with an authenticator with a 401 — a refusal by status, a
// progression by meaning — so spendCode decided it had failed and drew "That did
// not work" above a form saying the code was right. What separates the two is
// not the flag on its own, because a wrong authenticator code comes back with
// the same flag set and that one really is an error. The function takes the
// step as well, and the component trusts it rather than working the same
// condition out a second time.
//
// WHAT IS NOT ON THIS SCREEN.
//
// A password field, because there is no password. A "remember me" tick, because
// what it would remember is the token and session/store.ts argues where that may
// live. A link to create an account, because access is assigned by Nova per
// person and there is no self-service route. And no message anywhere that
// distinguishes an address with an account from one without: the server answers
// identically and this repeats its answer verbatim rather than improving on it.

import { DefList, NoticeBox, RehearsalMark } from "@nova/desk-ui/parts";
import { NovaWordmark } from "@nova/desk-ui/logos";
import { useEffect, useState, type ReactNode } from "react";
import { CONSOLE_MARK_PARTS, SERVER_SENTENCE } from "../build/thisBuild.js";
import { ThemeToggle } from "../theme/theme.js";
import { createSession, narrateRefusal, requestCode, stepAfterRefusal, type SignInStep } from "../api/signIn.js";
import { FACTOR_FIELD } from "./Authorise.js";
import { SignInSteps } from "./SignInSteps.js";
import type { ApiOptions } from "../api/client.js";
import type { Session } from "../session/store.js";

export type SignInProps = {
  readonly api: ApiOptions;
  readonly onSignedIn: (session: Session) => void;
  /**
   * Keep a session that has proved both factors, before step 4 is drawn
   * (Gabe, 13 Oct): written where every sign-in is written, so a reload on
   * "Both factors proved" opens the console rather than the sign-in form.
   * Absent, step 4 holds it in memory only.
   */
  readonly onKeep?: ((session: Session) => void) | undefined;
  /**
   * Why the person is looking at this screen, where there is a reason.
   *
   * Set when a session ended under them rather than when they arrived. A login
   * form that appears with no explanation after somebody was reading a report is
   * a form they will fill in twice before wondering what happened.
   */
  readonly because: string | null;
  /**
   * The address to start from, where the person has just signed out to sign
   * in again (the authenticator gate's last step). Typed by them a minute ago,
   * so offering it back is showing them their own typing.
   */
  readonly email?: string | null;
};

/**
 * Whether an address is one a mail server will take, as the design checks it
 * before sending (console-gate.jsx; Gabe, 12 Oct, decision C13): something,
 * an @, something, a dot, something, and no spaces. A shape, not a lookup:
 * it says nothing about whether the address has an account, which the service
 * still answers identically for every address that passes it.
 */
export function addressLooksSendable(typed: string): boolean {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(typed.trim());
}

/** The design's sentence under an address that fails the shape. */
export const ADDRESS_REFUSED = "That is not an address the mail server will accept.";

export function SignIn({ api, onSignedIn, onKeep, because, email: resume = null }: SignInProps): ReactNode {
  const [step, setStep] = useState<SignInStep>({ step: "address" });
  const [email, setEmail] = useState(resume ?? "");
  // Whether the address field has been left, so the shape is judged after
  // somebody has finished typing rather than at their first letter.
  const [touched, setTouched] = useState(false);
  const badAddress = email.trim() !== "" && !addressLooksSendable(email);
  // The step-4 panel: a session that has proved both factors, held until
  // "Open the console" is pressed. Only a sign-in that passed the
  // authenticator here reaches it; one that proved the code alone goes straight
  // on, to the authenticator gate where it has none.
  const [opened, setOpened] = useState<Session | null>(null);
  const [lostDevice, setLostDevice] = useState(false);
  const [code, setCode] = useState("");
  const [totp, setTotp] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [said, setSaid] = useState<string | null>(null);

  useEffect(() => {
    const root = document.querySelector(".console-gate");
    const vv = window.visualViewport;
    if (!(root instanceof HTMLElement) || vv == null) return undefined;

    const bump = () => {
      const covered = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      root.style.paddingBottom = `${Math.max(104, 24 + covered)}px`;
      const focused = document.activeElement;
      if (focused instanceof HTMLElement && focused.matches("input, textarea")) {
        focused.scrollIntoView({ block: "center" });
      }
    };

    vv.addEventListener("resize", bump);
    vv.addEventListener("scroll", bump);
    bump();
    return () => {
      vv.removeEventListener("resize", bump);
      vv.removeEventListener("scroll", bump);
      root.style.paddingBottom = "";
    };
  }, []);

  async function askForCode(): Promise<void> {
    // Not sent for an address that is not one (C13). The button is already
    // dead; this is the line that would send it, so it asks again.
    if (!addressLooksSendable(email)) {
      setTouched(true);
      return;
    }
    setBusy(true);
    setProblem(null);
    const result = await requestCode(api, email);
    setBusy(false);

    if (!result.ok) {
      setProblem(result.detail);
      return;
    }

    setSaid(result.detail);
    setCode("");
    setStep({ step: "code", email });
  }

  async function spendCode(): Promise<void> {
    if (step.step === "address") return;

    setBusy(true);
    setProblem(null);
    setSaid(null);

    // THE CODE COMES FROM THE STEP WHERE THERE IS ONE ON IT.
    //
    // On the second-factor step that is the code the server already told us was
    // right, held rather than retyped, because api/src/auth/login.ts does not
    // spend the emailed code on the refusal that asks for an authenticator.
    // Reading it out of the text field instead would work today and would break
    // the moment the field is cleared for any reason — and the symptom would be
    // "two emails per sign-in", which reads as a server fault.
    const submitted = step.step === "second_factor" ? step.code : code;

    const result = await createSession(api, {
      email: step.email,
      code: submitted,
      // Sent only on the step that has one. On the first attempt the key is
      // absent from the request body altogether, so the request shape is
      // identical for every account whether or not it has a factor.
      ...(step.step === "second_factor" ? { totp } : {}),
    });
    setBusy(false);

    if (result.ok) {
      if (step.step === "second_factor" && result.session.secondFactorProved === true) {
        onKeep?.(result.session);
        setOpened(result.session);
        return;
      }
      onSignedIn(result.session);
      return;
    }

    // WHETHER THAT WAS A FAILURE OR A STEP FORWARD, ASKED OF THE STATE MACHINE
    // RATHER THAN WORKED OUT AGAIN HERE. narrateRefusal and stepAfterRefusal
    // take the same inputs and check.mjs drives every transition through both,
    // so a future edit that taught one about a new refusal and not the other is
    // red rather than a screen that narrates a step forward as a failure.
    //
    // No sentence of our own on a progression, and none of the server's either.
    // Its refusal here is a paragraph that says the code was right, that an
    // authenticator is wanted, and that Nova can reset the factor — all three of
    // which the second-factor form below already says in its own lede and its
    // own help text. Repeating it would put the same paragraph on the screen
    // twice, which is the shape of the bug being fixed rather than a smaller
    // version of it. Every refusal that is a refusal still carries the server's
    // words.
    const narration = narrateRefusal(step, result);
    if (narration.kind === "refusal") setProblem(narration.detail);

    setTotp("");
    // BOTH FIELDS CLEARED, AND THE CODE ONE IS NOT TIDINESS.
    //
    // On a plain refusal the code was wrong, so leaving it in the box invites
    // somebody to press Sign in again on the same six digits. On the
    // second-factor refusal it has been proved right and is now held on the step,
    // and leaving a copy in form state would mean the credential lives in two
    // places that have to agree — which is precisely how "read the code from the
    // field instead of from the step" stays working until the day something
    // clears the field, and then produces two emails per sign-in and reads as a
    // server fault.
    setCode("");
    setStep(stepAfterRefusal(step, submitted, result));
  }

  return (
    <div className="frame console-root console-desk">
      {/* The same three-piece strip the signed-in shell draws, and the same
          reason it is here at all: a login form is where somebody decides
          whether they are in the right place, so a console that only admits to
          being the rehearsal one after you have signed in has told you too
          late. */}
      {CONSOLE_MARK_PARTS === null ? null : (
        <RehearsalMark
          label={CONSOLE_MARK_PARTS.label}
          sentence={CONSOLE_MARK_PARTS.sentence}
          server={CONSOLE_MARK_PARTS.server}
          className="console-mark"
        />
      )}

      <main className="gate console-page console-gate">
        {/* Hard top-right of the whole screen, not of the box. Somebody who
            cannot read this page in the mode it opened in has to be able to fix
            that before signing in, and a control tucked inside the panel is one
            more thing between them and the form. It is the only control on the
            gate that is not part of signing in, so it sits outside the panel
            that is. */}
        <div className="console-gate-theme">
          <ThemeToggle />
        </div>

        <div className="gate-box console-gate-box">
          {/* Nova's mark and not a typeset title. "Nova XP console" as an h1 was
              doing two jobs — saying whose service this is and heading the form
              — and the design separates them: the wordmark says whose, and the
              panel's own heading says what this step wants. */}
          <span className="brand">
            <NovaWordmark />
          </span>

          {/* Above the panel rather than inside it, because it is a standing
              fact about the screen and the panel holds the step. Drawn on all
              three steps and identically for every address — see the header. */}
          <SignInSteps step={step} opened={opened !== null} />

          <div className="panel panel-stack gate-panel">
            {because === null ? null : (
              <NoticeBox kind="amber" title="You were signed out">
                {sessionEndedCopy(because)}
              </NoticeBox>
            )}

            {problem === null ? null : (
              // `role="alert"` here, unlike the server mark, because this is an
              // event: it happened because of what the person just did, and a
              // screen reader should say it without being asked. NoticeBox sets
              // role="status", which is the wrong one for this box alone, so
              // this one stays hand-built.
              <div className="notice notice-bad" role="alert">
                <h3>Check that and try again</h3>
                <p>{problem}</p>
              </div>
            )}

            {said === null || opened !== null ? null : <NoticeBox kind="good">{said}</NoticeBox>}

            {opened !== null ? (
              // STEP 4, THE DESIGN'S "BOTH FACTORS PROVED" (console-gate.jsx).
              // What was proved and until when, and one action, before the
              // desk is drawn.
              <div className="panel-stack">
                <div>
                  <h1 className="gate-h">Both factors proved</h1>
                </div>
                <DefList
                  rows={[
                    ["Signed in as", opened.email ?? (step.step === "address" ? email : step.email)],
                    ["Proved", "Passcode and authenticator"],
                    ["Session ends", endsAt(opened.expiresAt)],
                  ]}
                />
                <div className="acts gate-acts">
                  <button type="button" className="btn" onClick={() => onSignedIn(opened)}>
                    Open the console
                  </button>
                </div>
              </div>
            ) : step.step === "address" ? (
              <form
                className="panel-stack"
                onSubmit={(event) => {
                  event.preventDefault();
                  void askForCode();
                }}
              >
                <div>
                  <h1 className="gate-h">Sign in</h1>
                  <p className="help help-tight">
                    Enter the email address linked to your Nova access. We will send you a sign-in code.
                  </p>
                </div>

                <label className="field">
                  <span className="label">Email address</span>
                  <input
                    id="console-email"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    onBlur={() => setTouched(true)}
                    onKeyDown={submitOnEnter(() => void askForCode(), busy || email.trim() === "" || badAddress)}
                    placeholder="you@novaexhibits.co.uk"
                    className={touched && badAddress ? "input input-bad" : "input"}
                    aria-invalid={touched && badAddress}
                    {...(touched && badAddress ? { "aria-describedby": "console-email-bad" } : {})}
                    required
                  />
                </label>
                {/* THE ADDRESS'S SHAPE IS CHECKED HERE NOW (C13, 12 Oct), in the
                    design's words, after the field is left. Only the shape: an
                    address that passes is still told the same thing whether or
                    not it has an account, which is the sentence below. */}
                {touched && badAddress ? (
                  <p className="gate-err" id="console-email-bad">
                    {ADDRESS_REFUSED}
                  </p>
                ) : null}

                <div className="acts gate-acts">
                  <button type="submit" className="btn" disabled={busy || email.trim() === "" || badAddress}>
                    {busy ? "Sending…" : "Email me a code"}
                  </button>
                </div>
                {/* The design's sentence, and the reason there is no "no such
                    account" message anywhere on this page. */}
                <p className="help" style={{ margin: 0 }}>
                  An address without an account is told the same thing as one with an account. That is
                  on purpose.
                </p>
              </form>
            ) : null}

            {opened === null && step.step === "code" ? (
              <form
                className="panel-stack"
                onSubmit={(event) => {
                  event.preventDefault();
                  void spendCode();
                }}
              >
                <div>
                  <h1 className="gate-h">Check your email</h1>
                  <p className="help help-tight">
                    We sent a six-digit code to <strong className="gate-strong">{step.email}</strong>.
                    It expires in ten minutes and works once.
                  </p>
                </div>

                <label className="field">
                  <span className="label">Code from the email</span>
                  <input
                    id="console-code"
                    className="input gate-code"
                    // `text` rather than `number`, because a number input strips a
                    // leading zero and a six-digit code can start with one. It
                    // also adds spinner arrows to a field where increment means
                    // nothing.
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={code}
                    onChange={(event) => setCode(event.target.value)}
                    onKeyDown={submitOnEnter(() => void spendCode(), busy || code.trim() === "")}
                    required
                  />
                  <span className="help help-tight">
                    A new code replaces the previous one.
                  </span>
                </label>

                <div className="acts gate-acts">
                  <button type="submit" className="btn" disabled={busy || code.trim() === ""}>
                    {busy ? "Checking…" : "Sign in"}
                  </button>
                </div>

                {/* ONE PRIMARY ACTION, AND THE OTHER TWO ARE QUIET. Both were
                    buttons the same size and weight as Sign in, which made a
                    step with one obvious next move look like a step with three.

                    THE RESEND IS THE BUTTON THE SERVER'S OWN REFUSAL ASKED FOR
                    AND THIS SCREEN DID NOT HAVE. A code lives ten minutes, and
                    somebody who goes to find their phone can come back to an
                    expired one. The service answers that with a paragraph ending
                    "Request another and try again" — and the only way to do that
                    used to be a button labelled "Use a different address", which
                    is not what the person wants to do and reads as though the
                    address were the thing that had gone wrong.

                    It is the same call the address step makes, so the resend
                    rules stay the service's: asking again supersedes the live
                    code, which is 006's `login_code_one_live` rather than
                    anything decided here, and the reply is the same frozen
                    sentence for every address. Nothing about pressing it says
                    whether the account exists.

                    Buttons rather than links, still. Both of these do something
                    rather than go somewhere, and an anchor that runs a handler
                    makes a promise about the back button that the handler
                    breaks. .btn-link is the look, not the element. */}
                <div className="gate-alt">
                  <button
                    type="button"
                    className="btn-link"
                    disabled={busy}
                    onClick={() => void askForCode()}
                  >
                    Email me another code
                  </button>
                  <button
                    type="button"
                    className="btn-link"
                    disabled={busy}
                    onClick={() => {
                      setStep({ step: "address" });
                      setSaid(null);
                      setProblem(null);
                    }}
                  >
                    Use a different address
                  </button>
                </div>
              </form>
            ) : null}

            {opened === null && step.step === "second_factor" ? (
              <form
                className="panel-stack"
                autoComplete="off"
                onSubmit={(event) => {
                  event.preventDefault();
                  void spendCode();
                }}
              >
                <div>
                  <h1 className="gate-h">Authenticator</h1>
                  <p className="help help-tight">
                    Email confirmed. Enter the current code from your authenticator app.
                  </p>
                </div>

                <label className="field">
                  <span className="label">Authenticator code</span>
                  <input
                    id="console-totp"
                    className="input gate-code"
                    type="text"
                    inputMode="numeric"
                    {...FACTOR_FIELD}
                    name="xp-factor"
                    maxLength={8}
                    value={totp}
                    onChange={(event) => setTotp(event.target.value)}
                    onKeyDown={submitOnEnter(() => void spendCode(), busy || totp.trim() === "")}
                    required
                  />
                  <span className="help help-tight">
                    If you no longer have the device, ask Nova to reset your authenticator.
                  </span>
                </label>

                <div className="acts gate-acts">
                  <button type="submit" className="btn" disabled={busy || totp.trim() === ""}>
                    {busy ? "Checking…" : "Sign in"}
                  </button>
                </div>

                {/* The design's quiet link on step 3. There is no self-service
                    reset and no recovery code, so it says who resets one. */}
                <div className="gate-alt">
                  <button type="button" className="btn-link" onClick={() => setLostDevice(true)}>
                    I no longer have the device
                  </button>
                </div>
                {lostDevice ? (
                  <NoticeBox kind="amber" title="Ask Nova to reset your authenticator">
                    <p>Nova resets a factor for you. There are no recovery codes anywhere in XP1.</p>
                  </NoticeBox>
                ) : null}
              </form>
            ) : null}
          </div>
        </div>
      </main>

      <footer className="foot console-footer gate-foot">
        <p>{SERVER_SENTENCE}</p>
      </footer>
    </div>
  );
}

/** When a session ends, in the reader's own clock, or the service's words where they do not parse. */
function endsAt(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

function sessionEndedCopy(detail: string): string {
  if (/not a live session|not the admin token/i.test(detail)) {
    return "Your session ended. Sign in again to keep working.";
  }
  return detail;
}

function submitOnEnter(action: () => void, blocked: boolean) {
  return (event: { key: string; preventDefault: () => void }): void => {
    if (event.key !== "Enter" || blocked) return;
    event.preventDefault();
    action();
  };
}
